import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';

const STATUS_OPTIONS = [
  'Empty in Customer', 'Available for Pickup', 'Need Appt', 'On the way to Customer',
  'Important Customer', 'Delivered to Customer', 'In Yard', 'Container Returned',
  'On the way to Return', 'Delivered to Exam Location', 'New Shipment',
  'Empty Container in Yard', 'On the way to Pickup', 'Shipment Closed', 'Trouble',
];

const STATUS_COLORS = {
  'Empty in Customer': '#FFDAB9',
  'Available for Pickup': '#90EE90',
  'Need Appt': '#1a1a1a',
  'On the way to Customer': '#FFB6C1',
  'Important Customer': '#A0522D',
  'Delivered to Customer': '#FFC0CB',
  'In Yard': '#DDA0DD',
  'Container Returned': '#D3D3D3',
  'On the way to Return': '#AFEEEE',
  'Delivered to Exam Location': '#8B4513',
  'New Shipment': '#E6E6FA',
  'Empty Container in Yard': '#FFFFE0',
  'On the way to Pickup': '#228B22',
  'Shipment Closed': '#ADD8E6',
  'Trouble': '#FA8072',
};

const DARK_TEXT_STATUSES = ['Need Appt', 'Important Customer', 'Delivered to Exam Location', 'On the way to Pickup'];

// Status Change side panel — picks a new status + driver + date + message,
// then hands the payload to the parent.
//
// iter 265.5 — Rewritten from `Radix Dialog modal={false}` to a plain
// positioned `<aside>`. Radix Dialog even in non-modal mode registers
// nested Radix Select popovers as "outside" its DismissableLayer, which
// silently blocked clicks on `<SelectItem>`s (New Status / Assign Driver
// dropdowns) in production. Users reported: "SELECTION FOR DROPDOWN
// DOESN'T WORK, CAN'T SELECT NEW STATUS OR EVEN DRIVER". This panel does
// not need modal semantics (no focus trap, no overlay, dashboard behind
// remains interactive), so a plain positioned <aside> is the right
// primitive and side-steps the Radix interop bug.
export const StatusChangeDialog = ({
  open, onOpenChange, newStatus, setNewStatus, selectedDriverId, setSelectedDriverId,
  statusMessage, setStatusMessage, statusDate, setStatusDate, onConfirm, masterData, api,
}) => {
  const [localDrivers, setLocalDrivers] = React.useState([]);

  React.useEffect(() => {
    const fetchDriversIfNeeded = async () => {
      if (open && (!masterData?.drivers || masterData.drivers.length === 0)) {
        try {
          const response = await api?.get('/master/drivers');
          if (response?.data) setLocalDrivers(response.data);
        } catch (error) {
          console.error('StatusChangeDialog: Failed to fetch drivers:', error);
        }
      } else if (masterData?.drivers?.length > 0) {
        setLocalDrivers(masterData.drivers);
      }
    };
    fetchDriversIfNeeded();
  }, [open, masterData?.drivers, api]);

  const drivers = localDrivers.length > 0 ? localDrivers : (masterData?.drivers || []);

  React.useEffect(() => {
    if (!open) return;
    const onEsc = (e) => { if (e.key === 'Escape') onOpenChange(false); };
    window.addEventListener('keydown', onEsc);

    // iter 265.5 (3) — The parent EditLoadSheet is a Radix Dialog (Sheet)
    // in modal mode, which sets `pointer-events: none` on <body>. Our
    // portalled panel + the Radix Select popover it opens are both
    // rendered under body, so they inherit the lockdown and every click
    // is silently swallowed. While THIS panel is open we hoist body's
    // pointerEvents back to auto and restore on close. Concurrent modal
    // Sheets still see themselves as pointer-events:auto since they set
    // it on their own content, so nothing else regresses.
    const previous = document.body.style.pointerEvents;
    document.body.style.pointerEvents = 'auto';

    return () => {
      window.removeEventListener('keydown', onEsc);
      document.body.style.pointerEvents = previous;
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  const panel = (
    <>
      {/* iter 265.5 (4) — The Radix Select popover renders into a
          `[data-radix-popper-content-wrapper]` element at z-50. Our status
          panel is z-100, so option rows sit BEHIND the panel and normal
          clicks hit the panel instead of the option. Only Select popovers
          open while the panel is mounted are affected. */}
      <style>{`
        body > div[data-radix-popper-content-wrapper] { z-index: 110 !important; }
      `}</style>
      <aside
      data-testid="status-change-dialog"
      role="dialog"
      aria-modal="false"
      aria-label="Change Status"
      className="fixed left-4 sm:left-8 top-4 sm:top-20 z-[100] w-[calc(100vw-2rem)] sm:w-full sm:max-w-md rounded-lg border border-slate-800 bg-slate-900 p-6 shadow-2xl max-h-[92vh] overflow-y-auto"
      onPointerDownCapture={(e) => e.stopPropagation()}
    >
      <div className="flex flex-col space-y-1.5">
        <h2 className="text-white text-lg font-semibold">Change Status</h2>
        <p className="text-slate-400 text-sm">
          Select a new status, assign a driver, and set a date for this shipment.
        </p>
      </div>
      <button
        type="button"
        onClick={() => onOpenChange(false)}
        className="absolute right-4 top-4 rounded-sm text-slate-400 hover:text-white focus:outline-none"
        data-testid="status-change-dialog-close"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label className="text-slate-300">New Status *</Label>
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="status-select">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800 max-h-[300px]">
              {STATUS_OPTIONS.map(status => (
                <SelectItem key={status} value={status} className="text-white">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded border border-slate-600"
                      style={{ backgroundColor: STATUS_COLORS[status] || '#94a3b8' }}
                    />
                    {status}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {newStatus && (
            <div
              className="p-2 rounded text-sm font-medium mt-2"
              style={{
                backgroundColor: STATUS_COLORS[newStatus],
                color: DARK_TEXT_STATUSES.includes(newStatus) ? '#fff' : '#1a1a1a',
              }}
            >
              Preview: {newStatus}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-slate-300">Assign Driver ({drivers.length} available)</Label>
          <Select
            value={selectedDriverId || 'none'}
            onValueChange={(v) => setSelectedDriverId(v === 'none' ? '' : v)}
            key={`driver-${drivers.length}`}
          >
            <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="driver-select">
              <SelectValue placeholder="Select driver (optional)" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800">
              <SelectItem value="none" className="text-slate-400">No driver</SelectItem>
              {drivers.length > 0 ? (
                drivers.map(driver => (
                  <SelectItem key={driver.id} value={driver.id} className="text-white">
                    {driver.name} {driver.code ? `(${driver.code})` : ''}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-drivers" disabled className="text-slate-500">Loading drivers...</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-slate-300">Date</Label>
          <Input
            type="date"
            value={statusDate || ''}
            onChange={(e) => setStatusDate(e.target.value)}
            className="bg-slate-950 border-slate-800 text-white"
            data-testid="status-date-input"
          />
          {statusDate && newStatus && (
            <p className="text-xs text-slate-400">
              {newStatus === 'Delivered to Customer' && 'Will set as Delivery Date'}
              {newStatus === 'Container Returned' && 'Will set as Return Date'}
              {newStatus === 'Available for Pickup' && 'Will set as Pickup Date'}
              {!['Delivered to Customer', 'Container Returned', 'Available for Pickup'].includes(newStatus) && 'Will set as Status Change Date'}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-slate-300">Message (optional)</Label>
          <Textarea
            value={statusMessage}
            onChange={(e) => setStatusMessage(e.target.value)}
            placeholder="Add a note about this status change..."
            className="bg-slate-950 border-slate-800 text-white min-h-[80px]"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-slate-800 mt-2">
        <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700">Cancel</Button>
        <Button onClick={onConfirm} className="bg-blue-600 hover:bg-blue-500" data-testid="confirm-status-change-btn">
          Update Status
        </Button>
      </div>
    </aside>
    </>
  );

  // iter 265.5 fix — Render at document.body via portal so we escape the
  // parent EditLoadSheet (Radix Sheet) overlay layer. The Sheet overlay has
  // pointer-events:auto which was intercepting every click on our select
  // triggers, X, and Cancel button.
  return typeof document !== 'undefined' ? createPortal(panel, document.body) : panel;
};
